import { useState, type FormEvent } from 'react';
import type {
  CaseType,
  EngagementLetterApplyRequest,
  EngagementLetterApplyResponse,
  ExtractedEngagementLetter,
  ExtractedField,
  SuggestedClientMatch,
} from '@stfors/shared-types';
import { ApiError } from '../../api/client';
import { useApplyEngagementLetter } from '../../api/queries/legal-document-ingest';
import { useLookups, activeOptionsFor } from '../../api/queries/lookups';
import { MoneyInput } from '../MoneyInput';
import { LookupSelect } from '../LookupSelect';
import { DateInput } from '../DateInput';
import { PhoneInput } from '../PhoneInput';
import { formatPhoneDisplay, stripPhoneForStorage } from '../../utils/phone';
import type { IngestMode } from './EngagementLetterIngest';
import { Toggle } from '../Toggle';

interface RateRow {
  description: string;
  unit: string;
  amount: string;
  amount_qualifier: string;
  notes: string;
}

// Split "Gregory Bernstein" → { first: "Gregory", last: "Bernstein" }.
function splitName(full: string | null): { first: string; last: string } {
  const t = (full ?? '').trim();
  if (!t) return { first: '', last: '' };
  const parts = t.split(/\s+/);
  if (parts.length === 1) return { first: '', last: parts[0]! };
  return { first: parts.slice(0, -1).join(' '), last: parts[parts.length - 1]! };
}

// Base64-encode the file for the JSON apply body (single-digit-MB letters).
async function fileToBase64(file: File): Promise<string> {
  const bytes = new Uint8Array(await file.arrayBuffer());
  let binary = '';
  const CHUNK = 0x8000;
  for (let i = 0; i < bytes.length; i += CHUNK) {
    binary += String.fromCharCode(...bytes.subarray(i, i + CHUNK));
  }
  return btoa(binary);
}

// Amber "check" chip rendered next to fields the parser is unsure about.
function Flag({ field }: { field: ExtractedField<unknown> }): JSX.Element | null {
  if (field.confidence === 'high') return null;
  return (
    <span className="eli-flag" title="Low confidence — please double-check this value">
      check
    </span>
  );
}

interface Props {
  mode: IngestMode;
  file: File;
  extracted: ExtractedEngagementLetter;
  suggestedClients: SuggestedClientMatch[];
  onApplied: (res: EngagementLetterApplyResponse) => void;
  onCancel: () => void;
  onBack: () => void;
}

export function EngagementLetterReview(props: Props): JSX.Element {
  const { mode, file, extracted: x, suggestedClients } = props;
  const isExisting = mode.kind === 'existing_project';
  const apply = useApplyEngagementLetter();
  const phoneLookups = useLookups('phone_type');
  const phoneTypes = activeOptionsFor(phoneLookups.data?.lookup_options, 'phone_type');

  const initialName = splitName(x.attorney_contact_name.value);
  const initialPhone = formatPhoneDisplay(x.phone.value);

  // --- Client + contact ---
  const [companyName, setCompanyName] = useState(x.law_firm.value ?? '');
  const [firstName, setFirstName] = useState(initialName.first);
  const [lastName, setLastName] = useState(initialName.last);
  const [email, setEmail] = useState(x.email.value ?? '');
  const [address, setAddress] = useState(x.address.value ?? '');
  const [phone, setPhone] = useState(initialPhone === '—' ? '' : initialPhone);
  const [phoneType, setPhoneType] = useState('Office');
  const [linkMode, setLinkMode] = useState<'create' | 'existing'>('create');
  const [linkClientId, setLinkClientId] = useState<number | null>(suggestedClients[0]?.id ?? null);

  // --- Project / matter ---
  const [matterTitle, setMatterTitle] = useState(x.matter_name.value ?? '');
  const [caseType, setCaseType] = useState<CaseType>('forensic_imaging');
  const [billingType, setBillingType] = useState<'hourly' | 'flat_fee' | 'retainer'>('hourly');
  const [flatFeeAmount, setFlatFeeAmount] = useState('');
  const [jurisdiction, setJurisdiction] = useState(x.governing_law.value ?? '');
  const [targetEndDate, setTargetEndDate] = useState<number | null>(x.service_window_end.value);

  // --- Engagement metadata ---
  const [effectiveDate, setEffectiveDate] = useState<number | null>(x.effective_date.value);
  const [serviceStart, setServiceStart] = useState<number | null>(x.service_window_start.value);
  const [serviceEnd, setServiceEnd] = useState<number | null>(x.service_window_end.value);
  const [serviceText, setServiceText] = useState(x.service_window_text.value ?? '');
  const [governingLaw, setGoverningLaw] = useState(x.governing_law.value ?? '');
  const [sfsSigner, setSfsSigner] = useState(x.sfs_signer_name.value ?? '');
  const [clientSigner, setClientSigner] = useState(x.client_signer_name.value ?? '');
  const [attorneySigner, setAttorneySigner] = useState(x.attorney_signer_name.value ?? '');
  const [clientSignedDate, setClientSignedDate] = useState<number | null>(x.client_signed_date.value);
  const [attorneySignedDate, setAttorneySignedDate] = useState<number | null>(x.attorney_signed_date.value);
  const [scopeSummary, setScopeSummary] = useState(x.scope_summary.value ?? '');
  const [isConfidential, setIsConfidential] = useState(true);

  // --- Rates ---
  const [rates, setRates] = useState<RateRow[]>(
    x.rates.map((r) => ({
      description: r.description,
      unit: r.unit ?? '',
      amount: r.amount != null ? String(r.amount) : '',
      amount_qualifier: r.amount_qualifier ?? '',
      notes: '',
    })),
  );

  const [error, setError] = useState<string | null>(null);

  const setRate = (i: number, patch: Partial<RateRow>): void => {
    setRates((rs) => rs.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  };
  const addRate = (): void =>
    setRates((rs) => [...rs, { description: '', unit: '', amount: '', amount_qualifier: '', notes: '' }]);
  const removeRate = (i: number): void => setRates((rs) => rs.filter((_, idx) => idx !== i));

  const onSubmit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setError(null);
    if (!lastName.trim()) {
      setError('Contact last name is required.');
      return;
    }
    if (!matterTitle.trim()) {
      setError('Matter / title is required.');
      return;
    }
    if (!isExisting && billingType === 'flat_fee' && !/^\d+(\.\d{1,2})?$/.test(flatFeeAmount.trim())) {
      setError('Enter a valid flat-fee amount (e.g. 5000.00).');
      return;
    }

    let pdfBase64: string;
    try {
      pdfBase64 = await fileToBase64(file);
    } catch {
      setError('Could not read the file.');
      return;
    }

    const projectFields = isExisting
      ? {}
      : {
          case_type: caseType,
          billing_type: billingType,
          ...(billingType === 'flat_fee' ? { flat_fee_amount: flatFeeAmount.trim() } : {}),
        };

    const body: EngagementLetterApplyRequest = {
      target: isExisting
        ? { mode: 'existing_project', project_id: mode.projectId }
        : { mode: 'new_project' },
      client_link:
        !isExisting && linkMode === 'existing' && linkClientId != null
          ? { mode: 'existing', client_id: linkClientId }
          : { mode: 'create' },
      company_name: companyName.trim() || null,
      first_name: firstName.trim() || null,
      last_name: lastName.trim(),
      email: email.trim() || null,
      address: address.trim() || null,
      phone: stripPhoneForStorage(phone),
      phone_type: phoneType,
      matter_title: matterTitle.trim(),
      ...projectFields,
      jurisdiction: jurisdiction.trim() || null,
      target_end_date: targetEndDate,
      effective_date: effectiveDate,
      service_window_start: serviceStart,
      service_window_end: serviceEnd,
      service_window_text: serviceText.trim() || null,
      governing_law: governingLaw.trim() || null,
      sfs_signer_name: sfsSigner.trim() || null,
      client_signer_name: clientSigner.trim() || null,
      attorney_signer_name: attorneySigner.trim() || null,
      client_signed_date: clientSignedDate,
      attorney_signed_date: attorneySignedDate,
      scope_summary: scopeSummary.trim() || null,
      engagement_notes: x.rate_notes ?? null,
      rates: rates
        .map((r) => ({
          description: r.description.trim(),
          unit: r.unit.trim() || null,
          amount: Number(r.amount.replace(/[^0-9.]/g, '')),
          amount_qualifier: r.amount_qualifier.trim() || null,
          notes: r.notes.trim() || null,
        }))
        .filter((r) => r.description.length > 0 && Number.isFinite(r.amount)),
      is_confidential: isConfidential,
      pdf_base64: pdfBase64,
      original_filename: file.name,
    };

    try {
      const res = await apply.mutateAsync(body);
      props.onApplied(res);
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        setError("You don't have permission to apply this engagement letter.");
        return;
      }
      const detail =
        err instanceof ApiError && err.body && typeof err.body === 'object' && 'error' in err.body
          ? String((err.body as { error: unknown }).error)
          : '';
      setError(`Could not apply the engagement letter. ${detail}`.trim());
    }
  };

  return (
    <form className="eli-review" onSubmit={onSubmit}>
      <div className="eli-review-head">
        <div>
          <span className="eli-review-title">Review extracted details</span>
          <span className="eli-review-file">{file.name}</span>
        </div>
        <button type="button" className="lgd-link" onClick={props.onBack} disabled={apply.isPending}>
          ← Use a different file
        </button>
      </div>
      <p className="eli-review-hint">
        Fields the parser was unsure about are marked <span className="eli-flag">check</span>. Edit anything
        before saving — nothing is written until you confirm.
      </p>

      {/* ---- Client ---- */}
      <fieldset className="eli-group">
        <legend>Client {isExisting ? '(updates this project’s client)' : ''}</legend>

        {!isExisting && suggestedClients.length > 0 && (
          <div className="eli-link-box">
            <label className="eli-radio">
              <input
                type="radio"
                checked={linkMode === 'create'}
                onChange={() => setLinkMode('create')}
              />
              Create a new client
            </label>
            <label className="eli-radio">
              <input
                type="radio"
                checked={linkMode === 'existing'}
                onChange={() => setLinkMode('existing')}
              />
              Link to existing
            </label>
            {linkMode === 'existing' && (
              <select
                className="form-input eli-link-select"
                value={linkClientId ?? ''}
                onChange={(e) => setLinkClientId(e.target.value ? Number(e.target.value) : null)}
              >
                {suggestedClients.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.company_name ?? c.primary_person_name}
                    {c.email ? ` — ${c.email}` : ''}
                  </option>
                ))}
              </select>
            )}
          </div>
        )}

        <div className="eli-grid">
          <label className="lgd-field">
            <span>Company / law firm <Flag field={x.law_firm} /></span>
            <input className="form-input" value={companyName} onChange={(e) => setCompanyName(e.target.value)} />
          </label>
          <label className="lgd-field">
            <span>Email <Flag field={x.email} /></span>
            <input className="form-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
          </label>
          <label className="lgd-field eli-col-2">
            <span>Address <Flag field={x.address} /></span>
            <input className="form-input" value={address} onChange={(e) => setAddress(e.target.value)} />
          </label>
        </div>
      </fieldset>

      {/* ---- Contact ---- */}
      <fieldset className="eli-group">
        <legend>Primary contact</legend>
        <div className="eli-grid">
          <label className="lgd-field">
            <span>First name</span>
            <input className="form-input" value={firstName} onChange={(e) => setFirstName(e.target.value)} />
          </label>
          <label className="lgd-field">
            <span>Last name * <Flag field={x.attorney_contact_name} /></span>
            <input className="form-input" value={lastName} onChange={(e) => setLastName(e.target.value)} required />
          </label>
          <label className="lgd-field">
            <span>Phone <Flag field={x.phone} /></span>
            <PhoneInput value={phone} onChange={setPhone} />
          </label>
          <label className="lgd-field">
            <span>Phone type</span>
            <select className="form-input" value={phoneType} onChange={(e) => setPhoneType(e.target.value)}>
              {(phoneTypes.length > 0 ? phoneTypes.map((o) => o.value) : ['Office', 'Mobile', 'Home', 'Fax', 'Other']).map(
                (t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ),
              )}
            </select>
          </label>
        </div>
      </fieldset>

      {/* ---- Project ---- */}
      <fieldset className="eli-group">
        <legend>{isExisting ? 'Project (this matter)' : 'New project'}</legend>
        <div className="eli-grid">
          <label className="lgd-field eli-col-2">
            <span>Matter / title * <Flag field={x.matter_name} /></span>
            <input className="form-input" value={matterTitle} onChange={(e) => setMatterTitle(e.target.value)} required />
          </label>
          {!isExisting && (
            <>
              <label className="lgd-field">
                <span>Case type</span>
                <LookupSelect
                  category="case_type"
                  value={caseType}
                  onChange={(v) => setCaseType(v as CaseType)}
                  className="form-input"
                />
              </label>
              <label className="lgd-field">
                <span>Billing</span>
                <LookupSelect
                  category="billing_type"
                  value={billingType}
                  onChange={(v) => setBillingType(v as 'hourly' | 'flat_fee' | 'retainer')}
                  className="form-input"
                />
              </label>
              {billingType === 'flat_fee' && (
                <label className="lgd-field">
                  <span>Flat fee amount *</span>
                  <MoneyInput
                    placeholder="5000.00"
                    value={flatFeeAmount}
                    onChange={setFlatFeeAmount}
                  />
                </label>
              )}
            </>
          )}
          <label className="lgd-field">
            <span>Jurisdiction <Flag field={x.governing_law} /></span>
            <input className="form-input" value={jurisdiction} onChange={(e) => setJurisdiction(e.target.value)} />
          </label>
          <label className="lgd-field">
            <span>Target end date <Flag field={x.service_window_end} /></span>
            <DateInput value={targetEndDate} onChange={setTargetEndDate} />
          </label>
        </div>
      </fieldset>

      {/* ---- Engagement ---- */}
      <fieldset className="eli-group">
        <legend>Engagement</legend>
        <div className="eli-grid">
          <label className="lgd-field">
            <span>Effective date <Flag field={x.effective_date} /></span>
            <DateInput value={effectiveDate} onChange={setEffectiveDate} />
          </label>
          <label className="lgd-field">
            <span>Service window (text) <Flag field={x.service_window_text} /></span>
            <input className="form-input" value={serviceText} onChange={(e) => setServiceText(e.target.value)} />
          </label>
          <label className="lgd-field">
            <span>Service start <Flag field={x.service_window_start} /></span>
            <DateInput value={serviceStart} onChange={setServiceStart} />
          </label>
          <label className="lgd-field">
            <span>Service end <Flag field={x.service_window_end} /></span>
            <DateInput value={serviceEnd} onChange={setServiceEnd} />
          </label>
          <label className="lgd-field">
            <span>Governing law <Flag field={x.governing_law} /></span>
            <input className="form-input" value={governingLaw} onChange={(e) => setGoverningLaw(e.target.value)} />
          </label>
          <label className="lgd-field">
            <span>SFS signer <Flag field={x.sfs_signer_name} /></span>
            <input className="form-input" value={sfsSigner} onChange={(e) => setSfsSigner(e.target.value)} />
          </label>
          <label className="lgd-field">
            <span>Client signer <Flag field={x.client_signer_name} /></span>
            <input className="form-input" value={clientSigner} onChange={(e) => setClientSigner(e.target.value)} />
          </label>
          <label className="lgd-field">
            <span>Attorney signer <Flag field={x.attorney_signer_name} /></span>
            <input className="form-input" value={attorneySigner} onChange={(e) => setAttorneySigner(e.target.value)} />
          </label>
          <label className="lgd-field">
            <span>Client signed date</span>
            <DateInput value={clientSignedDate} onChange={setClientSignedDate} />
          </label>
          <label className="lgd-field">
            <span>Attorney signed date</span>
            <DateInput value={attorneySignedDate} onChange={setAttorneySignedDate} />
          </label>
          <label className="lgd-field eli-col-2">
            <span>Scope summary <Flag field={x.scope_summary} /></span>
            <textarea
              className="form-input lgd-textarea"
              rows={3}
              value={scopeSummary}
              onChange={(e) => setScopeSummary(e.target.value)}
            />
          </label>
        </div>
      </fieldset>

      {/* ---- Rates ---- */}
      <fieldset className="eli-group">
        <legend>Rate sheet</legend>
        <p className="eli-rate-hint">Auto-extracted from the rate sheet — verify the amounts.</p>
        <div className="eli-rate-table">
          <div className="eli-rate-head">
            <span>Description</span>
            <span>Unit</span>
            <span>Amount</span>
            <span>+/-</span>
            <span />
          </div>
          {rates.map((r, i) => (
            <div className="eli-rate-row" key={i}>
              <input
                className="form-input"
                value={r.description}
                onChange={(e) => setRate(i, { description: e.target.value })}
                placeholder="Service description"
              />
              <input
                className="form-input"
                value={r.unit}
                onChange={(e) => setRate(i, { unit: e.target.value })}
                placeholder="Per Hour"
              />
              <MoneyInput
                value={r.amount}
                onChange={(next) => setRate(i, { amount: next })}
                placeholder="0.00"
              />
              <input
                className="form-input"
                value={r.amount_qualifier}
                onChange={(e) => setRate(i, { amount_qualifier: e.target.value })}
                placeholder="+"
              />
              <button
                type="button"
                className="eli-rate-del"
                onClick={() => removeRate(i)}
                aria-label="Remove rate"
                title="Remove"
              >
                ×
              </button>
            </div>
          ))}
        </div>
        <button type="button" className="lgd-link" onClick={addRate}>
          + Add rate
        </button>
      </fieldset>

      <Toggle
        checked={isConfidential}
        onChange={setIsConfidential}
        label="Mark the archived PDF confidential (recommended for signed letters)"
        className="eli-checkbox"
      />

      {error && <p className="lgd-error" role="alert">{error}</p>}

      <div className="eli-submit-row">
        <button type="button" className="lgd-link" onClick={props.onCancel} disabled={apply.isPending}>
          Cancel
        </button>
        <button type="submit" className="btn-cyan-primary" disabled={apply.isPending}>
          {apply.isPending
            ? 'Saving…'
            : isExisting
              ? 'Apply to project & archive'
              : 'Create project & archive'}
        </button>
      </div>
    </form>
  );
}
